import React from 'react';
import { Form, Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { TextField, ClassificatorField, NumberField } from '../../atoms';
import { getLoadingSelector } from '../../utils/asyncHelpers';
import { DeleteOutlined } from '@ant-design/icons';
import { Row, Col, Button, Spin } from 'antd';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormActions } from '../../components';
import { FORM_CLASSIFIERS_SUB_VALUES_EDIT } from '../../constants/formKeys';
import { getClassifierSubValuesChange } from '../../reducers/classifiersReducer';
import * as actions from '../../actions/actionCreators/classifiersActionCreators';
import {
  classifierGeneralItemField,
  classifierGeneralItemLangField,
  classifierSubValuesRawMaterialPropertiesItemField,
} from '../../constants/classifier';
import validateSchema from './validateSchema';
import classifiersStore from '../../utils/classifiersStore';
import FormStateToRedux from '../FormStateToRedux';
import { makeRffValidateFromLegacy } from '../rffLegacyValidateAdapter';

const ClassifierSubValuesEdit = ({
  t,
  onComplete,
  onCancel,
  isLoading,
  isEdit,
  classifierCode,
  isFeedstock,
  subId,
  initialValues: initialValuesProp,
  changeClassifierSubValues,
  fetchClassifierSubValues,
}) => {
  // Convert Immutable objects to plain JS (parent passes lang and rawMaterialProperties as Immutable)
  const initialValues = initialValuesProp
    ? {
        ...initialValuesProp,
        [classifierGeneralItemField.LANG]: initialValuesProp[
          classifierGeneralItemField.LANG
        ]?.toJS
          ? initialValuesProp[classifierGeneralItemField.LANG].toJS()
          : initialValuesProp[classifierGeneralItemField.LANG] || {},
        rawMaterialProperties: initialValuesProp.rawMaterialProperties?.toJS
          ? initialValuesProp.rawMaterialProperties.toJS()
          : initialValuesProp.rawMaterialProperties || [],
      }
    : {};

  const validate = makeRffValidateFromLegacy(
    validateSchema,
    { t, isFeedstock },
    initialValues
  );

  const onSubmit = async (values) => {
    await changeClassifierSubValues(subId, values);
    fetchClassifierSubValues(subId);
    onComplete();
    await classifiersStore.update();
  };

  const renderRawMaterialProperties = ({ fields, form }) => (
    <div>
      {fields.map((fieldName, index) => {
        const item = fields.value[index];
        const isDeleted =
          item?.[
            classifierSubValuesRawMaterialPropertiesItemField.DELETE_PENDING
          ];

        if (isDeleted) return null;
        return (
          <Row key={index} gutter={16}>
            <Col span={6}>
              <Field
                name={`${fieldName}.${classifierSubValuesRawMaterialPropertiesItemField.LAND_USE_CATEGORY}`}
                component={ClassificatorField}
                classificatorType={ClassificatorField.types.landUseCategory}
              />
            </Col>
            <Col span={6}>
              <Field
                name={`${fieldName}.${classifierSubValuesRawMaterialPropertiesItemField.LAND_USE_EMISSIONS}`}
                component={NumberField}
                precision={0}
              />
            </Col>
            <Col span={6}>
              <Field
                name={`${fieldName}.${classifierSubValuesRawMaterialPropertiesItemField.BIOFUEL_TYPE}`}
                component={ClassificatorField}
                classificatorType={ClassificatorField.types.biofuelType}
              />
            </Col>
            <Col span={5}>
              <Field
                name={`${fieldName}.${classifierSubValuesRawMaterialPropertiesItemField.CALCULATED_MULTIPLIER}`}
                component={NumberField}
                precision={0}
              />
            </Col>
            <Col span={1}>
              <Button
                value={index}
                onClick={() => {
                  if (item?.id) {
                    form.change(
                      `${fieldName}.${classifierSubValuesRawMaterialPropertiesItemField.DELETE_PENDING}`,
                      true
                    );
                  } else {
                    fields.remove(index);
                  }
                }}
                icon={<DeleteOutlined />}
                style={{
                  height: '32px',
                  width: '32px',
                  padding: '0px',
                  margin: '0px',
                  textAlign: 'center',
                  fontWeight: 'bolder',
                  border: 'none',
                }}
              />
            </Col>
          </Row>
        );
      })}
      <Button onClick={() => fields.push({})}>
        {t('buttonAddRawMaterialProperties')}
      </Button>
    </div>
  );

  return (
    <Spin spinning={isLoading}>
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
        mutators={{ ...arrayMutators }}
        render={({ handleSubmit, form }) => (
          <form onSubmit={handleSubmit}>
            <FormStateToRedux form={FORM_CLASSIFIERS_SUB_VALUES_EDIT} />
            <Row gutter={32}>
              {isFeedstock && (
                <>
                  <Col span={12}>
                    <Field
                      name={classifierGeneralItemField.CODE}
                      label={t('fieldCode')}
                      component={TextField}
                      disabled={isEdit}
                    />
                  </Col>
                  <Col span={12}>
                    <Field
                      name={classifierGeneralItemField.AIB_CODE}
                      label={t('tableGeneral.aibCode')}
                      component={TextField}
                    />
                  </Col>
                </>
              )}
              {!isFeedstock && (
                <Col span={24}>
                  <Field
                    name={classifierGeneralItemField.CODE}
                    label={t('fieldCode')}
                    component={TextField}
                    disabled={isEdit}
                  />
                </Col>
              )}
            </Row>
            <Row gutter={32}>
              <Col span={12}>
                <Field
                  name={`${classifierGeneralItemField.LANG}.${classifierGeneralItemLangField.EN}`}
                  label={t('fieldEn')}
                  component={TextField}
                />
              </Col>
              <Col span={12}>
                <Field
                  name={`${classifierGeneralItemField.LANG}.${classifierGeneralItemLangField.ET}`}
                  label={t('fieldEt')}
                  component={TextField}
                />
              </Col>
            </Row>
            <Row gutter={32}>
              <Col span={6} className="wideTableTitle">
                <h4>{t('tableRawMaterialProperties.landUseCategory')}</h4>
              </Col>
              <Col span={6} className="wideTableTitle">
                <h4>{t('tableRawMaterialProperties.landUseEmissions')}</h4>
              </Col>
              <Col span={6} className="wideTableTitle">
                <h4>{t('tableRawMaterialProperties.biofuelType')}</h4>
              </Col>
              <Col span={6} className="wideTableTitle">
                <h4>{t('tableRawMaterialProperties.calculatedMultiplier')}</h4>
              </Col>
            </Row>
            <FieldArray name="rawMaterialProperties">
              {(fieldArrayProps) =>
                renderRawMaterialProperties({ ...fieldArrayProps, form })
              }
            </FieldArray>
            <FormActions>
              <Button onClick={onCancel}>{t('buttonCancel')}</Button>
              <Button htmlType="submit" type="primary">
                {t('buttonSave')}
              </Button>
            </FormActions>
          </form>
        )}
      />
    </Spin>
  );
};

ClassifierSubValuesEdit.propTypes = {
  t: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool.isRequired,
  classifierCode: PropTypes.string,
  isFeedstock: PropTypes.bool.isRequired,
  subId: PropTypes.string,
  initialValues: PropTypes.object,
  changeClassifierSubValues: PropTypes.func.isRequired,
  fetchClassifierSubValues: PropTypes.func.isRequired,
};

const getIsLoading = getLoadingSelector(getClassifierSubValuesChange);

const mapStateToProps = (state) => ({
  isLoading: getIsLoading(state),
});

const mapDispatchToProps = {
  changeClassifierSubValues: actions.changeClassifierSubValues,
  fetchClassifierSubValues: actions.fetchClassifierSubValues,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ClassifierSubValuesEdit);
